import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Phone,
  Email,
  BusinessCenter,
  Search,
  Visibility,
  PersonAdd,
  Close,
  TrendingUp
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { MarketExecutive } from '../../types';

// Utility function to fix S3 URL format
const fixS3Url = (url: string | undefined): string | undefined => {
  if (!url) return undefined;

  // Check if it's an S3 URL with regional endpoint
  const s3RegionalPattern = /https:\/\/([^.]+)\.s3\.([^.]+)\.amazonaws\.com\/(.+)/;
  const match = url.match(s3RegionalPattern);

  if (match) {
    const bucketName = match[1];
    const path = match[3];

    // Convert to path-style URL (works better with CORS)
    const fixedUrl = `https://${bucketName}.s3.amazonaws.com/${path}`;
    console.log('🔧 Fixed S3 URL:', url, '->', fixedUrl);
    return fixedUrl;
  }

  return url;
};
const API_URL = process.env.REACT_APP_API_URL;

export default function MarketExecutivesPage() {
  const [marketExecutives, setMarketExecutives] = useState<MarketExecutive[]>([]);
  const [filteredExecutives, setFilteredExecutives] = useState<MarketExecutive[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedExecutive, setSelectedExecutive] = useState<MarketExecutive | null>(null);
  const [viewExecutive, setViewExecutive] = useState<MarketExecutive | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phoneNumber: '',
    region: '',
    employeeId: ''
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    password: '',
    phoneNumber: '',
    region: '',
    employeeId: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchMarketExecutives();
  }, []);

  const fetchMarketExecutives = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/auth/marketingusers`, {
        method: 'GET',
        headers: {
          'accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();

        // Map API response to MarketExecutive interface
        const mappedExecutives: MarketExecutive[] = data.map((executive: any) => ({
          id: executive.id,
          name: executive.full_name || 'N/A',
          email: executive.email || 'N/A',
          phone: executive.phoneNumber || '-',
          region: executive.region || '-',
          employeeId: executive.employee_id || '-',
          joiningDate: executive.created_at || new Date().toISOString().split('T')[0],
          status: executive.is_active ? 'active' : 'inactive',
          leadsCount: 0, // Not provided by API
          conversionsCount: 0, // Not provided by API
          performance: 'good', // Default value
          profile_image: executive.profile_image || '' // Add profile image
        }));

        setMarketExecutives(mappedExecutives);
        setFilteredExecutives(mappedExecutives);
      } else {
        setError('Failed to fetch market executives');
      }
    } catch (err) {
      setError('An error occurred while fetching market executives');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = marketExecutives;

    if (searchTerm) {
      filtered = filtered.filter(
        (executive) =>
          executive.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          executive.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((executive) => executive.status === statusFilter);
    }

    setFilteredExecutives(filtered);
  }, [searchTerm, statusFilter, marketExecutives]);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Full name is required';
        if (value.trim().length < 3) return 'Full name must be at least 3 characters';
        if (!/^[a-zA-Z\s.]+$/.test(value)) return 'Full name can only contain letters, spaces, and dots';
        return '';

      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email format';
        return '';

      case 'password':
        if (!value.trim()) return 'Password is required';
        if (value.length < 6) return 'Password must be at least 6 characters';
        return '';

      case 'phoneNumber':
        if (!value.trim()) return 'Phone number is required';
        const cleanPhone = value.replace(/[\s()-]/g, '');
        if (!/^[6-9]\d{9}$/.test(cleanPhone)) return 'Invalid Indian phone number. Must be 10 digits starting with 6-9';
        return '';

      case 'region':
        if (!value.trim()) return 'Region is required';
        if (value.trim().length < 3) return 'Region must be at least 3 characters';
        return '';

      case 'employeeId':
        if (!value.trim()) return 'Employee ID is required';
        if (value.trim().length < 3) return 'Employee ID must be at least 3 characters';
        return '';

      default:
        return '';
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    const error = validateField(field, value);
    setFormErrors({ ...formErrors, [field]: error });
  };

  const validateForm = (): boolean => {
    const errors = {
      name: validateField('name', formData.name),
      email: selectedExecutive ? '' : validateField('email', formData.email),
      password: '',
      phoneNumber: validateField('phoneNumber', formData.phoneNumber),
      region: '',
      employeeId: ''
    };

    setFormErrors(errors);
    return !Object.values(errors).some(error => error !== '');
  };

  const handleOpenDialog = (executive?: MarketExecutive) => {
    if (executive) {
      setSelectedExecutive(executive);
      setFormData({
        name: executive.name,
        email: executive.email,
        password: '',
        phoneNumber: executive.phone,
        region: executive.region,
        employeeId: executive.employeeId
      });
    } else {
      setSelectedExecutive(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        phoneNumber: '',
        region: '',
        employeeId: ''
      });
    }
    setFormErrors({
      name: '',
      email: '',
      password: '',
      phoneNumber: '',
      region: '',
      employeeId: ''
    });
    setError('');
    setSuccess('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedExecutive(null);
    setError('');
    setSuccess('');
    setSubmitting(false);
  };

  const handleViewExecutive = (executive: MarketExecutive) => {
    setViewExecutive(executive);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewExecutive(null);
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    // Validate form
    if (!validateForm()) {
      setError('Please fix all validation errors before submitting');
      return;
    }

    if (selectedExecutive) {
      // Update market executive - call update API
      try {
        setSubmitting(true);
        const response = await fetch(`${API_URL}/auth/users/${selectedExecutive.id}/marketinguser-info`, {
          method: 'PUT',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: formData.name,
            full_name: formData.name,
            phoneNumber: formData.phoneNumber
          })
        });

        if (response.ok) {
          setSuccess('Market Executive updated successfully!');
          setSubmitting(false);
          // Close dialog first, then refresh
          setTimeout(async () => {
            handleCloseDialog();
            await fetchMarketExecutives();
          }, 1500);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Failed to update market executive');
          setSubmitting(false);
        }
      } catch (err) {
        setError('An error occurred while updating the market executive');
        setSubmitting(false);
      }
    } else {
      // Add new market executive - call register API
      try {
        setSubmitting(true);
        const response = await fetch(`${API_URL}/auth/register`, {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: formData.name,
            email: formData.email,
            password: '',
            full_name: formData.name,
            phoneNumber: formData.phoneNumber,
            profile_image: '',
            region: '',
            employee_id: '',
            role: 'marketinguser'
          })
        });

        if (response.ok) {
          setSuccess('Market Executive registered successfully!');
          setSubmitting(false);
          // Close dialog first, then refresh
          setTimeout(async () => {
            handleCloseDialog();
            await fetchMarketExecutives();
          }, 1500);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Failed to register market executive');
          setSubmitting(false);
        }
      } catch (err) {
        setError('An error occurred while registering the market executive');
        setSubmitting(false);
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to remove this market executive?')) {
      try {
        const response = await fetch(`${API_URL}/auth/users/${id}`, {
          method: 'DELETE',
          headers: {
            'accept': 'application/json'
          }
        });

        if (response.ok) {
          setSuccess('Market Executive deleted successfully!');
          // Refresh the list
          await fetchMarketExecutives();
          setTimeout(() => setSuccess(''), 2000);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Failed to delete market executive');
          setTimeout(() => setError(''), 3000);
        }
      } catch (err) {
        setError('An error occurred while deleting the market executive');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'on_leave':
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Executive Name',
      width: 350,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            src={fixS3Url(params.row.profile_image)}
            sx={{
              bgcolor: params.row.profile_image ? 'transparent' : 'primary.main',
              width: 32,
              height: 32,
              border: params.row.profile_image ? '2px solid #e0e0e0' : 'none'
            }}
          >
            {!params.row.profile_image && params.value.charAt(0)}
          </Avatar>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      )
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 280,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Email fontSize="small" color="action" />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      )
    },
    {
      field: 'phone',
      headerName: 'Phone Number',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Phone fontSize="small" color="action" />
          <Typography variant="body2">{params.value || '-'}</Typography>
        </Box>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value.replace('_', ' ').toUpperCase()}
          color={getStatusColor(params.value) as any}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton size="small" color="primary" onClick={() => handleViewExecutive(params.row)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpenDialog(params.row)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row.id)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Global success/error messages */}
      {error && !openDialog && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && !openDialog && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Market Executives Management</Typography>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => handleOpenDialog()}
        >
          Add New Executive
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)'
          },
          gap: 3,
          mb: 3
        }}
      >
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" variant="body2">
                  Total Executives
                </Typography>
                <Typography variant="h4">{marketExecutives.length}</Typography>
              </Box>
              <BusinessCenter color="primary" sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" variant="body2">
                  Active Executives
                </Typography>
                <Typography variant="h4">
                  {marketExecutives.filter((e) => e.status === 'active').length}
                </Typography>
              </Box>
              <BusinessCenter color="success" sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>


      </Box>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search market executives..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1, minWidth: 250, maxWidth: 400 }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status Filter"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ height: 'calc(100vh - 520px)', minHeight: 400, width: '100%' }}>
            <DataGrid
              rows={filteredExecutives}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 }
                }
              }}
              pageSizeOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid rgba(224, 224, 224, 1)',
                },
                '& .MuiDataGrid-columnHeaders': {
                  borderBottom: '2px solid rgba(224, 224, 224, 1)',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '2px solid rgba(224, 224, 224, 1)',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                },
                '& .MuiDataGrid-virtualScroller': {
                  overflow: 'auto',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedExecutive ? 'Edit Market Executive' : 'Add New Market Executive'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, mt: 2 }}>
              {success}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              error={!!formErrors.name}
              helperText={formErrors.name || 'Enter full name'}
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              error={!!formErrors.email}
              helperText={formErrors.email || (selectedExecutive ? 'Cannot be changed' : 'Enter a valid email address')}
              required
              disabled={!!selectedExecutive}
              InputProps={{
                readOnly: !!selectedExecutive,
              }}
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={(e) => handleFieldChange('phoneNumber', e.target.value)}
              error={!!formErrors.phoneNumber}
              helperText={formErrors.phoneNumber || 'Enter 10 digit Indian phone number (e.g., 9876543210)'}
              required
              inputProps={{ maxLength: 10 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : selectedExecutive ? 'Update' : 'Register'}
            {!submitting && ' Executive'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Executive Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Market Executive Details</Typography>
          <IconButton onClick={handleCloseViewDialog} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {viewExecutive && (
            <Box>
              {/* Profile Section */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                <Avatar
                  src={fixS3Url(viewExecutive.profile_image)}
                  sx={{
                    width: 120,
                    height: 120,
                    mb: 2,
                    bgcolor: viewExecutive.profile_image ? 'transparent' : 'primary.main',
                    fontSize: '3rem',
                    border: '4px solid',
                    borderColor: 'primary.light'
                  }}
                >
                  {(!viewExecutive.profile_image && viewExecutive.name.charAt(0).toUpperCase()) || 'M'}
                </Avatar>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {viewExecutive.name}
                </Typography>
              </Box>

              {/* Details Grid */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 3,
                mb: 3
              }}>
                {/* Email */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Email color="primary" fontSize="small" />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Email Address
                    </Typography>
                  </Box>
                  <Typography variant="body1">{viewExecutive.email}</Typography>
                </Box>

                {/* Phone */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Phone color="primary" fontSize="small" />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Phone Number
                    </Typography>
                  </Box>
                  <Typography variant="body1">{viewExecutive.phone || '-'}</Typography>
                </Box>

                {/* Joining Date */}
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Joining Date
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {new Date(viewExecutive.joiningDate).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseViewDialog} variant="outlined">
            Close
          </Button>
          <Button
            onClick={() => {
              handleCloseViewDialog();
              if (viewExecutive) handleOpenDialog(viewExecutive);
            }}
            variant="contained"
            startIcon={<Edit />}
          >
            Edit Executive
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
